'use client';

import { defineMessages, useIntl } from 'react-intl';
import { RatingStars } from '@packages/shared/src/ui/Rating/RatingStars';
import type { MessageValue } from 'react-intl';

const messages = defineMessages<{
  readonly averageRating: { readonly rating: MessageValue };
  readonly reviewCount: { readonly count: MessageValue };
}>({
  averageRating: {
    id: 'productCard.averageRating',
    defaultMessage: 'Durchschnittliche Bewertung: {rating}',
  },
  reviewCount: {
    id: 'productCard.reviewCount',
    defaultMessage: '{count} Bewertungen',
  },
});

export type RatingDisplayProps = {
  /**
   * Average rating for a given product, ranging from 0 to 5 with a precision of 0.1.
   * */
  avgRating: number;
  /**
   * Amount of given ratings.
   * */
  reviewCount?: number;
};

/**
 * Display a star rating between 0 and 5, prioritizing performance and efficiency
 * */
const RatingDisplay = ({ avgRating, reviewCount }: RatingDisplayProps) => {
  const { formatMessage } = useIntl();

  // round to 2 decimal digits, like with the Rating component
  const roundedRating = Math.round(avgRating * 10) / 10;

  // messages are each used twice, we only need to format them once
  const ratingMessage = formatMessage(messages.averageRating, { rating: roundedRating });
  const reviewCountMessage = formatMessage(messages.reviewCount, { count: reviewCount });

  return (
    <div className="flex items-center gap-0.5">
      <RatingStars
        aria-label={ratingMessage}
        ratingValue={roundedRating}
        role="img"
        title={ratingMessage}
      />
      {reviewCount !== undefined && reviewCount > 0 && (
        <>
          <span
            aria-hidden
            className="text-size-body-xs text-color-text-dark"
            title={reviewCountMessage}
          >
            ({reviewCount})
          </span>
          <span className="sr-only">{reviewCountMessage}</span>
        </>
      )}
    </div>
  );
};

export { RatingDisplay };
